import React, { ReactNode, useId, useState } from "react"
import { closePopper, openPopper, usePopperDispatch } from "../../context/popper"

import FocusLock from "react-focus-lock"
import { Placement } from "@popperjs/core"
import PopperStyles from "./popper.module.css"
import classNames from "classnames"
import { useDelayedUnmounting } from "../../hooks/use-delayed-unmounting"
import { useMediaQuery } from "usehooks-ts"
import { useOnClickOutside } from "../../hooks/use-on-click-outside"
import { usePopper } from "react-popper"

type PopperProps = {
    modifiers?: any
    placement?: Placement
    customInput?: ({
        ref,
        onClick,
        triggerId,
        contentId,
    }: {
        ref: React.Dispatch<React.SetStateAction<Element | null>>
        onClick: () => void
        triggerId: string
        contentId: string
    }) => ReactNode
    children: ReactNode | ((close: () => void) => ReactNode)
    showOverflow?: boolean
    width?: number
    closePopper?: () => void
    left?: boolean
    triggerId?: string
    contentId?: string
}

const Popper = ({
    modifiers,
    placement,
    customInput,
    children,
    showOverflow,
    width: widthProp,
    closePopper: closePopperProp,
    left,
    triggerId,
    contentId,
}: PopperProps) => {
    const [showPopper, setShowPopper] = useState<boolean>(false)
    const [referenceElement, setReferenceElement] = useState<Element | null>(null)
    const [popperElement, setPopperElement] = useState<HTMLElement | null>(null)
    const [arrowElement, setArrowElement] = useState<Element | null>(null)
    const { styles, attributes } = usePopper(referenceElement, popperElement, {
        modifiers: [
            { name: "arrow", options: { element: arrowElement } },
            { name: "offset", options: { offset: [10, 10] } },
            { ...modifiers },
        ],
        placement: placement || "bottom-end",
    })
    const shouldRenderChild = useDelayedUnmounting(showPopper)

    const dispatch = usePopperDispatch()

    const setPopper = (visible: boolean) => {
        setShowPopper(visible)
        if (visible) return openPopper(dispatch)

        closePopper(dispatch)
        closePopperProp && closePopperProp()
    }

    useOnClickOutside(() => showPopper && setPopper(false), [referenceElement, popperElement], [showPopper])

    const isTablet = useMediaQuery("(max-width: 1023px)")

    const loclaTriggerId = useId()
    const loclaContentId = useId()

    triggerId = triggerId || loclaTriggerId
    contentId = contentId || loclaContentId

    return (
        <FocusLock
            disabled={!showPopper}
            returnFocus={suggestedNode => {
                if (suggestedNode.id === triggerId) return true

                return false
            }}
        >
            {customInput instanceof Function ? (
                customInput({ ref: setReferenceElement, onClick: () => setPopper(!showPopper), triggerId, contentId })
            ) : (
                <button
                    type="button"
                    ref={setReferenceElement}
                    onClick={() => setPopper(!showPopper)}
                    className={PopperStyles.button}
                    id={triggerId}
                    aria-expanded={showPopper}
                    aria-controls={contentId}
                    aria-haspopup="dialog"
                >
                    <i className="lavita-icon"></i>
                </button>
            )}
            {shouldRenderChild && (
                <div
                    ref={setPopperElement}
                    style={!isTablet ? styles.popper : undefined}
                    className={classNames(PopperStyles.popper, {
                        [PopperStyles.mounted]: showPopper,
                        [PopperStyles.left]: left,
                    })}
                    {...attributes.popper}
                    role="dialog"
                    id={contentId}
                    aria-labelledby={triggerId}
                    aria-hidden={!showPopper}
                >
                    <div ref={setArrowElement} style={styles.arrow} className={PopperStyles.triangle} />
                    <div className={PopperStyles.close}>
                        <button type="button" onClick={() => setPopper(false)}>
                            <i className="lavita-icon"></i>
                        </button>
                    </div>
                    <div
                        className={classNames(PopperStyles.content, {
                            [PopperStyles.showOverflow]: showOverflow,
                        })}
                        style={{ width: widthProp }}
                    >
                        {children instanceof Function ? children(() => setPopper(false)) : children}
                    </div>
                </div>
            )}
        </FocusLock>
    )
}

export default Popper
